/**
 * Demo approvals and policy for ?mock=1, built on the mock sessions so every card links to
 * a real-looking session, machine and project.
 */
import { DEFAULT_EXPIRY_POLICY, delegatedNote } from '@orgus/protocol';
import type { ApprovalHistoryEntry, ApprovalRequest, ApprovalsState, DelegateAgent, DelegatePolicy, Id, PolicyRule, PolicyState, Session } from '@orgus/protocol';
import { DEFAULT_RULES, classifyRisk, evaluate, subjectOf, suggestPattern, titleOf } from '@orgus/policy';
import { MOCK_BLOCKED, MOCK_IDS } from '../../lib/mock.ts';

const MIN = 60_000;

let seq = 0;
const mid = (p: string) => `${p}_DEMO${(++seq).toString(36).toUpperCase().padStart(4, '0')}`;

function request(s: Pick<Session, 'id' | 'machineId' | 'projectId' | 'cwd'>, tool: string, input: Record<string, unknown>, ageMs: number, now: number, timeoutMs: number, hint: ApprovalRequest['policyHint']): ApprovalRequest {
  const subject = subjectOf(tool, input);
  const createdAt = now - ageMs;
  return {
    id: mid('apr'),
    kind: 'tool',
    machineId: s.machineId,
    sessionId: s.id,
    projectId: s.projectId,
    missionId: null,
    title: titleOf(tool, input, s.cwd),
    tool,
    input,
    risk: classifyRisk({ projectId: s.projectId, tool, subject, cwd: s.cwd }),
    createdAt,
    expiresAt: createdAt + timeoutMs,
    state: 'pending',
    ...(subject ? { subject } : {}),
    cwd: s.cwd,
    suggestedPattern: suggestPattern(tool, subject, s.cwd),
    policyHint: hint,
  };
}

export function mockApprovals(sessions: Session[], now: number): ApprovalsState {
  const timeoutMs = 15 * MIN;
  const live = [...sessions].filter((s) => s.status !== 'ended').sort((a, b) => b.lastActivity - a.lastActivity);
  const pick = (i: number): Pick<Session, 'id' | 'machineId' | 'projectId' | 'cwd' | 'title'> =>
    live[i % Math.max(1, live.length)] ?? { id: 'demo-session', machineId: 'demo-machine', projectId: null, cwd: '/work/app', title: 'Demo session' };
  const s0 = pick(0);
  const s1 = pick(1);
  const s2 = pick(2);
  const pending: ApprovalRequest[] = [
    // A controlled session's request (ADR 0056): it can be handed back to the session's own dialog.
    { ...request(s0, 'Bash', { command: 'git push --force-with-lease origin fix/login-race', description: 'Update the PR branch after the rebase' }, 3 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'default:network', reason: 'It touches the network, so a person decides.' }), fallback: 'session' },
    request(s1, 'Bash', { command: 'pnpm add zod@^4.1.0', description: 'Add schema validation for the webhook payload' }, 7 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'default:network', reason: 'It touches the network, so a person decides.' }),
    request(
      s2,
      'Edit',
      { file_path: `${s2.cwd.replace(/\\/g, '/').replace(/\/[^/]+$/, '')}/shared-config/tsconfig.base.json`, old_string: '"strict": false,', new_string: '"strict": true,' },
      12 * MIN,
      now,
      timeoutMs,
      { decision: 'ask', ruleId: 'default:outside-worktree', reason: 'It writes outside the session folder, so a person decides.' },
    ),
  ];
  const done = (r: ApprovalRequest, change: Partial<ApprovalRequest>): ApprovalRequest => ({ ...r, ...change });
  const delegates = mockDelegates(s1.projectId, now);
  const policy = delegates[0];
  // Answered by the project's delegate agent (ADR 0074).
  const delegated: ApprovalRequest[] = policy
    ? [
        done(request(s1, 'Bash', { command: 'pnpm test:affected 2>&1 | tail -20' }, 6 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'pol_DEMO_ASK', reason: 'A person reads every command.' }), {
          state: 'allowed',
          decidedBy: 'agent:dev-agent',
          decidedAt: now - 6 * MIN,
          decidedFrom: 'delegate',
          scope: 'once',
          delegate: { policyId: policy.id, version: policy.version, agent: 'agent:dev-agent', class: 'tests' },
          note: delegatedNote({ policyId: policy.id, version: policy.version, agent: 'agent:dev-agent', class: 'tests' }),
        }),
      ]
    : [];
  const recent: ApprovalRequest[] = [
    ...delegated,
    done(request(s1, 'WebFetch', { url: 'https://docs.stripe.com/webhooks/signatures' }, 48 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'default:network' }), {
      state: 'allowed',
      decidedBy: 'owner',
      decidedAt: now - 47 * MIN,
      decidedFrom: 'Safari on iOS',
      scope: 'session',
    }),
    done(request(s0, 'Bash', { command: 'rm -rf ~/Library/Caches/pnpm' }, 95 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'default:outside-worktree' }), {
      state: 'denied',
      decidedBy: 'owner',
      decidedAt: now - 94 * MIN,
      decidedFrom: 'Chrome on Windows',
      note: 'Leave the global cache alone; clear node_modules in the repo instead.',
    }),
    done(request(s2, 'Bash', { command: 'npm publish --dry-run' }, 3 * 60 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'default:network' }), {
      state: 'expired',
      decidedBy: 'timeout',
      decidedAt: now - 3 * 60 * MIN + timeoutMs,
      decidedFrom: 'timeout',
      parked: true,
      note: 'No answer within 15 min',
    }),
    done(request(s1, 'Bash', { command: 'npm install' }, 5 * 60 * MIN, now, timeoutMs, { decision: 'ask', ruleId: 'default:network' }), {
      state: 'allowed',
      decidedBy: 'owner',
      decidedAt: now - 5 * 60 * MIN + 40_000,
      decidedFrom: 'Chrome on Windows',
      scope: 'project',
      ruleId: 'pol_DEMO_NPM',
    }),
  ];
  const blocking = mockBlocking(sessions, now, timeoutMs);
  const asks = mockAsks(sessions, now);
  return {
    pending: [...pending, ...asks.pending],
    recent: [...blocking, ...asks.recent, ...recent],
    blocking,
    grants: [],
    expiry: { org: { ...DEFAULT_EXPIRY_POLICY }, projects: { [MOCK_IDS.billing]: { onExpiry: 'hold', holdMaxMinutes: 120 } }, updatedAt: now - 2 * 24 * 60 * MIN, updatedBy: 'owner' },
    sessionAllows: [{ sessionId: s1.id, machineId: s1.machineId, tool: 'WebFetch', pattern: 'https://docs.stripe.com/*', by: 'owner', at: now - 47 * MIN }],
    timeoutMinutes: 15,
    delegates,
    // The demo shows a full Inbox, so people answer here; a real hub starts on Orgus (ORGU-81).
    answerer: { answerer: 'people' },
  };
}

/**
 * A burst from one session for the Inbox's bulk answers (ADR 0072): six requests within a minute,
 * reads and a search, a test run, a fetch and one high-risk delete. Seeded when the Inbox opens on
 * demo data (useMockBurst), so the other pages' demo counts stay as they are.
 */
export function mockBurst(sessions: readonly Session[], now: number): ApprovalRequest[] {
  const live = [...sessions].filter((s) => s.status !== 'ended').sort((a, b) => b.lastActivity - a.lastActivity);
  const s = live[1 % Math.max(1, live.length)];
  return s ? burst(s, now, 15 * MIN) : [];
}

/** Six requests a session made within a minute, with low, medium and high risk. */
function burst(s: Pick<Session, 'id' | 'machineId' | 'projectId' | 'cwd'>, now: number, timeoutMs: number): ApprovalRequest[] {
  const root = s.cwd.replace(/\\/g, '/');
  const hint = { decision: 'ask', ruleId: 'default:outside-worktree', reason: 'It reaches outside the session folder, so a person decides.' } as const;
  const calls: [string, Record<string, unknown>, number, ApprovalRequest['risk']][] = [
    ['Read', { file_path: `${root}/../shared-config/eslint.base.js` }, 70_000, 'low'],
    ['Read', { file_path: `${root}/../shared-config/tsconfig.base.json` }, 66_000, 'low'],
    ['Grep', { pattern: 'webhookSecret', path: `${root}/..` }, 62_000, 'low'],
    ['Bash', { command: 'pnpm vitest run src/webhooks', description: 'Run the webhook tests' }, 58_000, 'medium'],
    ['WebFetch', { url: 'https://github.com/stripe/stripe-node/blob/master/CHANGELOG.md' }, 54_000, 'medium'],
    ['Bash', { command: 'rm -rf ../shared-config/dist', description: 'Clear the stale shared build' }, 50_000, 'high'],
  ];
  return calls.map(([tool, input, age, risk]) => ({ ...request(s, tool, input, age, now, timeoutMs, hint), risk }));
}

/** One delegate policy on the project the demo's second session works in. */
export function mockDelegates(projectId: Id | null, now: number): DelegatePolicy[] {
  if (!projectId) return [];
  const created = now - 2 * 24 * 60 * MIN;
  return [
    {
      id: 'dlg_DEMO0001',
      projectId,
      agent: 'dev-agent',
      classes: ['edits', 'tests', 'typecheck', 'checks', 'git'],
      perMinute: 20,
      expiresAt: now + 5 * 24 * 60 * MIN,
      version: 2,
      createdBy: 'owner',
      createdAt: created,
      updatedBy: 'owner',
      updatedAt: now - 3 * 60 * MIN,
      answered: 142,
      lastAnsweredAt: now - 6 * MIN,
    },
  ];
}

/** The agent tokens the demo's policy editor offers. */
export const MOCK_DELEGATE_AGENTS: DelegateAgent[] = [
  { name: 'dev-agent', expiresAt: Date.now() + 20 * 24 * 60 * MIN, problem: null },
  { name: 'triage-bot', expiresAt: Date.now() + 9 * 24 * 60 * MIN, problem: 'It does not carry the approval.approve scope.' },
];

type AskSession = Pick<Session, 'id' | 'machineId' | 'projectId' | 'cwd'>;

/**
 * What Claude asks beyond a permission (R-HL-6, ADR 0056), one of each kind: two questions at
 * once (one of them multi-choice), a plan to approve, an MCP form and an MCP sign-in link, and
 * one question answered half an hour ago for Done. They come from sessions Orgus did not launch,
 * so each goes back to its session when nobody answers in time (fallback 'session').
 */
export function mockAsks(sessions: readonly Session[], now: number): { pending: ApprovalRequest[]; recent: ApprovalRequest[] } {
  const find = (id: Id, fallback: AskSession): AskSession => sessions.find((s) => s.id === id) ?? fallback;
  const held = find('5d212fa9-held-extras', { id: '5d212fa9-held-extras', machineId: MOCK_IDS.studio, projectId: MOCK_IDS.qawl, cwd: 'C:/Users/Srika/CascadeProjects/Qawl-Agents' });
  const memory = find('a4e2b9c7-memory', { id: 'a4e2b9c7-memory', machineId: MOCK_IDS.studio, projectId: MOCK_IDS.qawl, cwd: 'C:/tmp/qawl-wt-memory' });
  const stripe = find('7b3e5d20-stripe-retry', { id: '7b3e5d20-stripe-retry', machineId: MOCK_IDS.mbp, projectId: MOCK_IDS.billing, cwd: '/Users/srikanth/code/billing-service' });
  const erase = find('4d7c1b8e-artifact-erase', { id: '4d7c1b8e-artifact-erase', machineId: MOCK_IDS.studio, projectId: MOCK_IDS.qawl, cwd: 'C:/Users/Srika/CascadeProjects/Qawl-Agents' });
  const ask = (s: AskSession, kind: ApprovalRequest['kind'], tool: string, title: string, ageMs: number, waitMs: number, a: ApprovalRequest['ask']): ApprovalRequest => ({
    id: mid('apr'),
    kind,
    machineId: s.machineId,
    sessionId: s.id,
    projectId: s.projectId,
    missionId: null,
    title,
    tool,
    risk: kind === 'question' ? 'low' : 'medium',
    createdAt: now - ageMs,
    expiresAt: now - ageMs + waitMs,
    state: 'pending',
    cwd: s.cwd,
    fallback: 'session',
    ask: a,
  });
  const questions: ApprovalRequest['ask'] = {
    type: 'questions',
    questions: [
      {
        header: 'Database',
        question: 'Which database should the held-step migration run against first?',
        multiSelect: false,
        options: [
          { label: 'Staging', description: 'Matches the production schema and is safe to reset' },
          { label: 'Local Postgres', description: 'Fastest, but misses the partial indexes' },
          { label: 'Production replica', description: 'Read only, catches real data shapes' },
        ],
      },
      {
        header: 'Tests',
        question: 'Which suites should run before the pull request?',
        multiSelect: true,
        options: [
          { label: 'Unit', description: 'About 40 seconds' },
          { label: 'Integration', description: 'Needs the local queue running' },
          { label: 'End to end', description: 'About 6 minutes' },
        ],
      },
    ],
  };
  const pending = [
    ask(held, 'question', 'AskUserQuestion', 'Which database should the held-step migration run against first?', 8 * MIN, 15 * MIN, questions),
    ask(memory, 'plan', 'ExitPlanMode', 'Approve plan: Memory page, phase 1a: an operator view', 6 * MIN, 15 * MIN, {
      type: 'plan',
      planFilePath: 'C:/Users/Srika/.claude/plans/memory-operator-view.md',
      plan: [
        '# Memory page, phase 1a: an operator view',
        '',
        'Replace the flat memory list with a view of what the agent remembers, why, and when it last used it.',
        '',
        '## Steps',
        '',
        '1. Add `lastUsedAt` and `source` to the memory store, with a migration for existing rows.',
        '2. Group the list by source (conversation, file, tool) with counts in each group header.',
        '3. Add a detail drawer: the memory, where it came from, and the sessions that used it.',
        '4. Keep the old list behind `?legacy=1` for one release.',
        '',
        '## Files',
        '',
        '- `src/memory/store.ts`',
        '- `src/pages/Memory.tsx`',
        '- `src/memory/MemoryDrawer.tsx` (new)',
        '',
        '## Risks',
        '',
        '- The migration touches about 12k rows. It runs in batches of 500 and can resume.',
      ].join('\n'),
    }),
    ask(stripe, 'elicitation', 'mcp:linear', 'linear: Create an issue for the webhook retry follow-ups.', 4 * MIN, 15 * MIN, {
      type: 'elicitation',
      server: 'linear',
      mode: 'form',
      message: 'Create an issue for the webhook retry follow-ups.',
      schema: {
        properties: {
          title: { type: 'string', title: 'Title', default: 'Retry failed webhooks with backoff', maxLength: 120 },
          team: { type: 'string', title: 'Team', enum: ['BILL', 'PLAT', 'WEB'], enumNames: ['Billing', 'Platform', 'Web'] },
          priority: { type: 'integer', title: 'Priority', description: '1 is urgent, 4 is low.', minimum: 1, maximum: 4, default: 2 },
          assignee: { type: 'string', title: 'Assignee email', format: 'email' },
          notify: { type: 'boolean', title: 'Post in the team channel', default: true },
        },
        required: ['title', 'team'],
      },
    }),
    ask(erase, 'elicitation', 'mcp:github', 'github: Sign in to GitHub so the server can read the bucket audit issues.', 2 * MIN, 15 * MIN, {
      type: 'elicitation',
      server: 'github',
      mode: 'url',
      message: 'Sign in to GitHub so the server can read the bucket audit issues.',
      url: 'https://github.com/login/device',
    }),
  ];
  const answered: ApprovalRequest = {
    ...ask(held, 'question', 'AskUserQuestion', 'Which branch should the fix land on?', 34 * MIN, 10 * MIN, {
      type: 'questions',
      questions: [{ header: 'Branch', question: 'Which branch should the fix land on?', multiSelect: false, options: [{ label: 'main' }, { label: 'release/2.4' }] }],
    }),
    state: 'allowed',
    scope: 'once',
    decidedBy: 'owner',
    decidedAt: now - 33 * MIN,
    decidedFrom: 'Chrome on Windows',
    askAnswer: { answers: { 'Which branch should the fix land on?': 'main' } },
  };
  // Nobody answered in Orgus, so it went back to the session, and the person answered there
  // (ADR 0056, ORGU-3): three questions, one multi-choice and one in their own words.
  const card = find('b1f4c2e0-approvals', { id: 'b1f4c2e0-approvals', machineId: MOCK_IDS.studio, projectId: MOCK_IDS.qawl, cwd: 'C:/Users/Srika/CascadeProjects/Qawl-Agents' });
  const setup: ApprovalRequest['ask'] = {
    type: 'questions',
    questions: [
      { header: 'Hub', question: 'Which hub should hold the backlog and run the missions?', multiSelect: false, options: [{ label: 'Your hub on 4600 (Recommended)' }, { label: 'A new hub for this project' }] },
      { header: 'My access', question: 'How should I sign in to that hub to create items and start missions?', multiSelect: true, options: [{ label: 'Operator token' }, { label: 'Admin token' }, { label: 'Your browser session' }] },
      { header: 'Approvals', question: "Who answers the missions' approval requests?", multiSelect: false, options: [{ label: 'Me, every time' }, { label: 'A policy I write' }] },
    ],
  };
  const passedBack = (s: AskSession, a: ApprovalRequest['ask'], kind: ApprovalRequest['kind'], tool: string, title: string, ageMs: number): ApprovalRequest => ({
    ...ask(s, kind, tool, title, ageMs, 5 * MIN, a),
    state: 'expired',
    decidedBy: 'timeout',
    decidedFrom: 'timeout',
    decidedAt: now - ageMs + 5 * MIN,
    note: 'No answer in Orgus in time; Claude Code asked in the session.',
  });
  const inSession: ApprovalRequest = {
    ...passedBack(card, setup, 'question', 'AskUserQuestion', 'Which hub should hold the backlog and run the missions?', 21 * MIN),
    sessionAnswer: {
      outcome: 'answered',
      by: 'owner',
      at: now - 14 * MIN,
      toolUseId: 'toolu_mock_setup',
      answers: {
        'Which hub should hold the backlog and run the missions?': 'Your hub on 4600 (Recommended)',
        'How should I sign in to that hub to create items and start missions?': 'Operator token, Your browser session',
        "Who answers the missions' approval requests?": 'Me, within a policy that allows the safe list',
      },
    },
  };
  const request = find('c3d8a7f1-request-card', { id: 'c3d8a7f1-request-card', machineId: MOCK_IDS.studio, projectId: MOCK_IDS.qawl, cwd: 'C:/tmp/qawl-wt-approvals' });
  const stillAsked = passedBack(request, { type: 'plan', plan: '# Request card, inline in chat\n\n1. Render the card from the request.\n2. Land it in the Inbox.' }, 'plan', 'ExitPlanMode', 'Approve plan: Request card, inline in chat', 48 * MIN);
  return { pending, recent: [inSession, answered, stillAsked] };
}

/**
 * Decided requests that stopped a session (R-HL-3): the incident (a mission session asked to
 * publish, nobody answered, it stopped) and a person's denial with a note (a redirect): the
 * session followed the note and carried on, so its blocker leads with Mark as handled.
 */
export function mockBlocking(sessions: Session[], now: number, timeoutMs = 15 * MIN): ApprovalRequest[] {
  const find = (id: Id, fallback: Pick<Session, 'id' | 'machineId' | 'projectId' | 'cwd'>) => sessions.find((s) => s.id === id) ?? fallback;
  const release = find(MOCK_BLOCKED.expiredSession, { id: MOCK_BLOCKED.expiredSession, machineId: MOCK_IDS.studio, projectId: MOCK_IDS.orgus, cwd: 'C:/Users/Srika/CascadeProjects/dev-portal' });
  const ledger = find(MOCK_BLOCKED.deniedSession, { id: MOCK_BLOCKED.deniedSession, machineId: MOCK_IDS.mbp, projectId: MOCK_IDS.billing, cwd: '/Users/srikanth/code/billing-service' });
  const network = { decision: 'ask' as const, ruleId: 'default:network', reason: 'It touches the network, so a person decides.' };
  const expired = request(release, 'Bash', { command: 'npm publish --access public', description: 'Publish @orgus/sdk 0.3.0 to npm' }, 55 * MIN, now, timeoutMs, network);
  const denied = request(ledger, 'Bash', { command: 'psql "$PROD_DATABASE_URL" -f scripts/backfill-ledger-2026-03.sql', description: 'Run the March backfill' }, 30 * MIN, now, timeoutMs, network);
  return [
    {
      ...denied,
      id: MOCK_BLOCKED.deniedApproval,
      missionId: MOCK_BLOCKED.deniedMission,
      risk: 'high',
      state: 'denied',
      decidedBy: 'owner',
      decidedAt: now - 28 * MIN,
      decidedFrom: 'Safari on iOS',
      note: 'Not against production. Run it on staging first and show me the row counts.',
      carriedOnAt: now - 26 * MIN,
    },
    {
      ...expired,
      id: MOCK_BLOCKED.expiredApproval,
      missionId: MOCK_BLOCKED.expiredMission,
      risk: 'high',
      state: 'expired',
      decidedBy: 'timeout',
      decidedAt: expired.createdAt + timeoutMs,
      decidedFrom: 'timeout',
      parked: true,
      onExpiry: 'deny',
      note: 'No answer within 15 minutes',
    },
  ];
}

function rule(id: string, decision: PolicyRule['decision'], match: PolicyRule['match'], reason: string, scope: PolicyRule['scope'], ageMs: number, now: number, hits: number): PolicyRule {
  return { id, scope, match, decision, reason, createdBy: 'owner', createdAt: now - ageMs, hits };
}

export function mockPolicy(projectIds: Id[], now: number): PolicyState {
  const p = projectIds[0];
  const rules: PolicyRule[] = [
    rule('pol_DEMO_TEST', 'allow', { tool: 'Bash', pattern: 'pnpm test:*' }, 'Tests only read and write inside the repo.', { kind: 'org' }, 9 * 24 * 60 * MIN, now, 214),
    rule('pol_DEMO_NPM', 'allow', { tool: 'Bash', pattern: 'npm install' }, 'Made a rule by owner from an approval.', p ? { kind: 'project', projectId: p } : { kind: 'org' }, 5 * 60 * MIN, now, 6),
    rule('pol_DEMO_PUSH', 'ask', { tool: 'Bash', pattern: 'git push * main' }, 'Pushing to main always needs a person.', { kind: 'org' }, 20 * 24 * 60 * MIN, now, 3),
    rule('pol_DEMO_RM', 'deny', { tool: 'Bash', pattern: 'rm -rf *' }, 'Recursive deletes are refused; remove files one by one.', { kind: 'org' }, 20 * 24 * 60 * MIN, now, 1),
    rule('pol_DEMO_OPUS', 'deny', { action: 'session.start', model: 'opus*' }, 'Opus is for missions tagged hard.', { kind: 'org' }, 2 * 24 * 60 * MIN, now, 0),
  ];
  const hits: Record<string, number> = { 'default:read-only': 1832, 'default:in-worktree': 641, 'default:network': 37, 'default:outside-worktree': 5 };
  for (const r of rules) hits[r.id] = r.hits ?? 0;
  return { rules, version: 12, defaults: DEFAULT_RULES.map((d) => ({ ...d, hits: hits[d.id] ?? 0 })), hits, updatedAt: now - 5 * 60 * MIN };
}

/** What launched sessions asked for in the last week, for demo simulations. */
export function mockHistory(sessions: Session[], now: number): ApprovalHistoryEntry[] {
  const cmds = ['pnpm test', 'pnpm test --filter web', 'git status', 'git push origin main', 'npm install', 'curl -s https://api.github.com', 'rm -rf dist', 'pnpm lint', 'git push origin fix/a', 'node scripts/seed.mjs'];
  const out: ApprovalHistoryEntry[] = [];
  const list = sessions.length ? sessions : [{ id: 's', machineId: 'm', projectId: null, cwd: '/w' } as Session];
  for (let i = 0; i < 120; i++) {
    const s = list[i % list.length]!;
    const command = cmds[(i * 7) % cmds.length]!;
    out.push({ at: now - i * 83 * MIN, machineId: s.machineId, sessionId: s.id, projectId: s.projectId, tool: 'Bash', subject: command, cwd: s.cwd, title: `Run ${command}`, decision: 'allow', ruleId: null });
  }
  return out;
}

/** The demo's answer to "simulate": replays mockHistory against the current and candidate rules. */
export function simulateLocally(history: ApprovalHistoryEntry[], current: PolicyRule[], candidate: PolicyRule[], now: number) {
  const before = { allow: 0, ask: 0, deny: 0 };
  const after = { allow: 0, ask: 0, deny: 0 };
  const changed = [];
  for (const h of history) {
    const req = { projectId: h.projectId, tool: h.tool, subject: h.subject, cwd: h.cwd };
    const b = evaluate(current, req);
    const a = evaluate(candidate, req);
    before[b.decision]++;
    after[a.decision]++;
    if (a.decision !== b.decision) changed.push({ at: h.at, title: h.title, tool: h.tool, subject: h.subject, projectId: h.projectId, before: b.decision, after: a.decision, ruleId: a.ruleId });
  }
  return { from: now - 7 * 24 * 60 * MIN, to: now, total: history.length, before, after, changed: changed.slice(0, 200), changedCount: changed.length };
}
